import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { apiClient } from '../lib/api/client';

interface ConsolidatedTranscription {
  session_id: string;
  full_text: string;
  segments: Array<{
    id: number;
    speaker: string;
    text: string;
    start: number;
    end: number;
    call_number?: number;
    call_sid?: string;
  }>;
  call_count: number;
  total_words: number;
  duration_seconds: number;
  call_sids: string[];
  date_range: {
    first_call: string;
    last_call: string;
  };
}

export const MergedTranscriptionPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ConsolidatedTranscription | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTranscription();
  }, [sessionId]);

  const fetchTranscription = async () => {
    try {
      setLoading(true);
      const response = await apiClient.client.get(`/api/v1/transcriptions/merged/${sessionId}`);
      setData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch transcription');
    } finally {
      setLoading(false);
    }
  };

  const highlightText = (text: string) => {
    if (!searchTerm) return text;
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    return text.replace(regex, '<mark class="bg-yellow-200 dark:bg-yellow-500 dark:text-gray-900 rounded px-0.5">$1</mark>');
  };

  const exportTranscript = () => {
    if (!data) return;
    const blob = new Blob([data.full_text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript_${data.session_id}.txt`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-300">{error || 'Failed to load transcription'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Consolidated Transcription</h1>
            <p className="text-sm text-gray-600 dark:text-gray-300">{data.call_count} calls merged • {data.total_words} words</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={exportTranscript}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600"
            >
              📥 Export
            </button>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600"
            >
              ← Back
            </button>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white dark:bg-gray-800/80 rounded-lg border border-gray-200 dark:border-gray-600 p-4">
        <input
          type="text"
          placeholder="Search in transcript..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800/80 rounded-lg border border-gray-200 dark:border-gray-600 p-4">
          <div className="text-sm text-gray-600 dark:text-gray-300">Total Calls</div>
          <div className="text-2xl font-semibold text-gray-900 dark:text-white">{data.call_count}</div>
        </div>
        <div className="bg-white dark:bg-gray-800/80 rounded-lg border border-gray-200 dark:border-gray-600 p-4">
          <div className="text-sm text-gray-600 dark:text-gray-300">Total Words</div>
          <div className="text-2xl font-semibold text-gray-900 dark:text-white">{data.total_words}</div>
        </div>
        <div className="bg-white dark:bg-gray-800/80 rounded-lg border border-gray-200 dark:border-gray-600 p-4">
          <div className="text-sm text-gray-600 dark:text-gray-300">Duration</div>
          <div className="text-2xl font-semibold text-gray-900 dark:text-white">
            {Math.floor(data.duration_seconds / 60)}m {data.duration_seconds % 60}s
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800/80 rounded-lg border border-gray-200 dark:border-gray-600 p-4">
          <div className="text-sm text-gray-600 dark:text-gray-300">Date Range</div>
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            {new Date(data.date_range.first_call).toLocaleDateString()} -
            {new Date(data.date_range.last_call).toLocaleDateString()}
          </div>
        </div>
      </div>

      {/* Transcription */}
      <div className="bg-white dark:bg-gray-800/80 rounded-lg border border-gray-200 dark:border-gray-600 p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Full Transcript</h2>
        <div
          className="max-w-none whitespace-pre-wrap font-mono text-sm text-gray-800 dark:text-gray-100 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: highlightText(data.full_text) }}
        />
      </div>

      {/* Call Details */}
      <div className="bg-white dark:bg-gray-800/80 rounded-lg border border-gray-200 dark:border-gray-600 p-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Included Calls</h3>
        <div className="space-y-2">
          {data.call_sids.map((sid, index) => (
            <div key={sid} className="flex items-center gap-2 text-sm">
              <span className="text-gray-600 dark:text-gray-300">Call {index + 1}:</span>
              <code className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 px-2 py-1 rounded">{sid}</code>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

